import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// Components
import { Button, Clickable, Col, Icon, Input, Row, Text } from 'components';

// Utils
import { firebase, storage } from 'core';
import { Api, theme, validateEmail } from 'lib';

const SignupWithEmail = ({
  otherUser,
  onHandleGoBack,
  actionType,
  followAthlete = null,
  navigateTo = null
}) => {
  const { isMobile, language } = useSelector((state) => state.appState);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [emailErr, setEmailErr] = useState(null);
  const [isAPILoading, setIsAPILoading] = useState(false);
  // Optional, and deliberately left out of `disabled` below — an unticked box never blocks
  // registration.
  const [sponsorConsent, setSponsorConsent] = useState(false);
  // Fan.js and CallToActionModal can both mount this form on the Profile page, so the checkbox id
  // must be unique per instance for the label binding to hit the right box.
  const sponsorConsentId = useId();

  const disabled = password.length < 8 || name.length < 1 || !validateEmail(email);

  async function signUp() {
    const res = await Api.post(
      '/users/signUp',
      { name, email, password, lang: language, sponsorConsent },
      'signUp'
    );
    if (!res) return;
    if (res.err) return setEmailErr(res.err);

    storage.setItem('token', res.token);

    firebase.logEvent('web_sign_up_from_cta_share', { method: 'email', type: actionType });

    if (followAthlete && typeof followAthlete === 'function') {
      setIsAPILoading(true);
      await followAthlete();
      setIsAPILoading(false);
    }

    const isShoutout = actionType === 'requestShoutout' || actionType === 'sendShoutout';

    dispatch({
      type: 'SET_USER',
      payload: {
        ...res.user,
        atheleteIdToSubscribe: actionType === 'subscribe' ? otherUser?._id : null,
        athleteUsernameToFollow: actionType === 'follow' ? otherUser?.username : null,
        atheleteUsernameToFace2Face: actionType === 'face2face' ? otherUser?.username : null,
        atheleteUsernameToAdPlayaz: actionType === 'adPlayaz' ? otherUser?.username : null,
        athleteUsernameToShoutout: isShoutout ? otherUser?.username : null,
        fromAtheleteUsername: actionType === 'fromUser' ? otherUser?.username : null,
        followUpAction: actionType,
        navigateTo
      }
    });
  }

  function handleEmailChange(e) {
    setEmail(e.target.value);
    setEmailErr(null);
  }

  return (
    <>
      <Col centerAll wid="288px" marg="0 auto">
        <BackIconContainer>
          <Clickable onClick={onHandleGoBack}>
            <Icon type="arrowLeft" dimensions={16} col={theme.PRIMARY_BLACK} />
          </Clickable>
        </BackIconContainer>

        <Input
          marg={isMobile ? '32px 0 0 0' : '26px 0 0 0'}
          wid="100%"
          placeholder={t('common.email')}
          value={email}
          onChange={handleEmailChange}
          error={emailErr}
        />

        <Input
          marg="26px 0 0"
          wid="100%"
          placeholder={t('common.fullName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <Input
          marg="26px 0 0"
          wid="100%"
          password
          placeholder={t('common.password')}
          error={!!password.length && password.length < 8 && 'Must at least contain 8 characters.'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Row noFlex center wid="100%" marg="24px 0 0">
          <ConsentBox
            id={sponsorConsentId}
            type="checkbox"
            checked={sponsorConsent}
            onChange={(e) => setSponsorConsent(e.target.checked)}
          />

          <ConsentLabel htmlFor={sponsorConsentId}>
            <Text b2 spanned>
              {t('auth.sponsorConsent')}
            </Text>
          </ConsentLabel>
        </Row>

        <Button
          marg="26px 0 0"
          wid="100%"
          text={t('common.signUp')}
          bg={disabled ? theme.GREY_20 : theme.SECONDARY}
          app
          onClick={disabled ? undefined : signUp}
          action="signUp"
          loading={+isAPILoading}
        />
      </Col>
    </>
  );
};

const BackIconContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  padding: 21px;
  width: 14px;
  height: 14px;
`;

/*
  Same native checkbox as the main sign-up form (`screens/SignUp/components/Auth.js`): the shared
  `Checkbox` is sized for the marketing pages and spills over the label at this form's size.
*/
const ConsentBox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: ${theme.SECONDARY};
  cursor: pointer;
`;

/* The label is what most people click, so it carries the cursor and the `for` binding. */
const ConsentLabel = styled.label`
  margin-left: 10px;
  cursor: pointer;
`;

export default SignupWithEmail;
